import { SESSION_STORAGE_HYDRATED_EVENT } from "../hooks/use-navigation";
import { enforceOrgAdminBranchPolicy, syncMultiBranchFlag } from "./branchSession";

/** Org-level flags. Branch payroll rows default these to false and must not clobber session. */
const COMPANY_WIDE_SESSION_FIELDS = [
    "is_branch_enabled",
    "org_admin_branch_selection",
    "is_nabh",
    "modify_issued_documents",
    "loc_track_ctl",
    "birthday_alerts_enabled",
    "roster_shift_management_enabled",
    "unused_shift_period_days",
    "unused_shift_alert_enabled",
];

/**
 * Merge and persist custom configuration to session storage (and localStorage
 * when "Remember Me" is active). Dispatches SESSION_STORAGE_HYDRATED_EVENT so
 * navigation and other flag-gated UI refresh without logout/login.
 */
export function persistCustomConfig(config) {
    if (!config || typeof config !== "object") return;

    const existing = JSON.parse(sessionStorage.getItem("customConfig") || "{}");
    const incoming = { ...config };
    if (incoming.branch_id != null && incoming.branch_id !== "") {
        for (const key of COMPANY_WIDE_SESSION_FIELDS) {
            delete incoming[key];
        }
    }
    const merged = {
        ...existing,
        ...incoming,
    };
    const serialized = JSON.stringify(merged);
    sessionStorage.setItem("customConfig", serialized);

    if (localStorage.getItem("keepSignedIn")) {
        localStorage.setItem("customConfig", serialized);
    }

    if ("org_admin_branch_selection" in config) {
        enforceOrgAdminBranchPolicy();
    }

    window.dispatchEvent(new Event(SESSION_STORAGE_HYDRATED_EVENT));
}

/** Persist company-wide config + multi_branch from a dashboard payload. */
export function hydrateDashboardSession(data) {
    if (!data || typeof data !== "object") return;

    const configs = data.payroll_custom_configuration;
    const companyConfig = Array.isArray(configs) ? configs[0] : configs;
    if (companyConfig && typeof companyConfig === "object") {
        persistCustomConfig(companyConfig);
    }

    if (data.multi_branch !== undefined) {
        syncMultiBranchFlag(data.multi_branch);
        window.dispatchEvent(new Event(SESSION_STORAGE_HYDRATED_EVENT));
    }
}

/** True when employees must submit OT requests (approval mode). False = auto-calculate from attendance. */
export function isOvertimeApprovalRequired() {
    try {
        const cfg = JSON.parse(sessionStorage.getItem("customConfig") || "{}");
        if (cfg.overtime_enabled === false) return false;
        return cfg.overtime_approval_required !== false;
    } catch {
        return true;
    }
}

/** Show Overtime Request menu and Approval Inbox OT tab. */
export function isOvertimeRequestWorkflowEnabled() {
    return isOvertimeApprovalRequired();
}

/** True when Settings > Configuration has branch management turned on. */
export function isBranchManagementEnabled() {
    try {
        const cfg = JSON.parse(sessionStorage.getItem("customConfig") || "{}");
        return cfg.is_branch_enabled !== false;
    } catch {
        return true;
    }
}

/**
 * True when Settings > Configuration > Features & Access has Roster & shift
 * management turned on (FR-100). Defaults to FALSE, unlike the branch flag:
 * this feature is opt-in per company, so an absent value means off.
 */
export function isRosterShiftManagementEnabled() {
    try {
        const cfg = JSON.parse(sessionStorage.getItem("customConfig") || "{}");
        return cfg.roster_shift_management_enabled === true;
    } catch {
        return false;
    }
}

export const PAYROLL_PROCESS_LOCK_MESSAGE =
    "Payroll is being processed and is temporarily locked. Overtime, allowance, and deduction changes are not allowed until payroll is complete. Please contact HR if urgent.";

/** True when FR-042 payroll lock is active (Run Payroll / Settings). */
export function isPayrollProcessLocked() {
    try {
        const cfg = JSON.parse(sessionStorage.getItem("customConfig") || "{}");
        return !!cfg.lock_employee_salary_payslip;
    } catch {
        return false;
    }
}
